import * as S from "./styles";
import { useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import selectedImage from "~/assets/png/winning-spy-ad.png";
import roundCheckIcon from "~/assets/svg/check-success.svg";
import Modal from "../ui/Modal";
import { Icon } from "@fortawesome/fontawesome-svg-core";
import { faArrowRight } from "@fortawesome/pro-solid-svg-icons";
import { SVG } from "~/components/ui/SVG";
import { useAccount } from "~/hooks/useAccount";

const dropshiptoolLink =
  "https://app.dropshiptool.io/register?utm_source=alidrop&utm_medium=onboardingmodal&utm_campaign=findwinningads";

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );

  useEffect(() => {
    function onResize() {
      setIsMobile(window.innerWidth < breakpoint);
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
}

export function DTPromotionModal() {
  const isMobile = useIsMobile(908);
  const location = useLocation();
  const { account } = useAccount();

  const [isShowing, setIsShowing] = useState(false);
  const hasPlanId = useRef(false);

  // Show the DT banner once plan_id has been seen in the URL.
  // Wait for the upgrade-funnel modal to close first (it removes plan_id).
  // If the upgrade funnel won't show (coupon already used), show immediately.
  // For first-time subscribers, show directly (no upgrade funnel blocks them).
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const planIdPresent = !!queryParams.get("plan_id");
    const isFirstSubscription = queryParams.get("first_subscription") === "true";

    if (planIdPresent) {
      hasPlanId.current = true;
    }

    if (!hasPlanId.current) return;

    // Don't show on settings page — the redirect to home will trigger it there
    if (location.pathname.includes("settings")) return;

    // First-time subscriber with plan_id → show DT banner directly
    if (isFirstSubscription && planIdPresent) {
      setIsShowing(true);
      return;
    }

    // plan_id removed → upgrade funnel closed → show DT banner
    if (!planIdPresent) {
      setIsShowing(true);
      return;
    }

    // plan_id still present but upgrade funnel won't show → show DT banner
    if (account?.has_used_stripe_upgrade_funnel_coupon) {
      setIsShowing(true);
    }
  }, [location.search, account?.has_used_stripe_upgrade_funnel_coupon]);

  return (
    <Modal
      id="dt-promotion-funnel"
      minWidth={isMobile ? "90%" : "904px"}
      maxWidth={isMobile ? "" : "912px"}
      maxHeight={isMobile ? "" : "462px"}
      isShowing={isShowing}
      padding="0">
      <S.ModalContainer>
        <S.ContentGrid isMobile={isMobile}>
          <S.ImageWrapper isMobile={isMobile}>
            <S.Image
              src={selectedImage}
              isMobile={isMobile}
              alt="Winning Spy Ads"
            />
          </S.ImageWrapper>

          <S.TextContent isMobile={isMobile}>
            <S.Heading>
              Discover <S.BlueText>Winning Products</S.BlueText> and the Hottest{" "}
              <S.BlueText>Viral Ads</S.BlueText>
            </S.Heading>

            <S.Subheading>
              Find and monitor e-commerce stores to gain valuable insights into
              their revenue, sales trends, top products.
            </S.Subheading>

            <S.FeatureList>
              <S.FeatureItem>
                <img
                  src={roundCheckIcon}
                  alt="Checkmark"
                  width={20}
                  height={20}
                />
                <span>Get top facebook ad examples and creatives.</span>
              </S.FeatureItem>
              <S.FeatureItem>
                <img
                  src={roundCheckIcon}
                  alt="Checkmark"
                  width={20}
                  height={20}
                />
                <span>Track real products to find winners.</span>
              </S.FeatureItem>
              <S.FeatureItem>
                <img
                  src={roundCheckIcon}
                  alt="Checkmark"
                  width={20}
                  height={20}
                />
                <span>Discover top performing ads.</span>
              </S.FeatureItem>
            </S.FeatureList>

            <S.CtaButton
              isMobile={isMobile}
              onClick={() =>
                window.open(dropshiptoolLink, "_blank", "noopener,noreferrer")
              }>
              Start Free Trial
            </S.CtaButton>

            <S.ContinueButton
              isMobile={isMobile}
              onClick={() => setIsShowing(false)}>
              <span>Continue to dashboard</span>
              <SVG icon={faArrowRight as Icon} size="sm" />
            </S.ContinueButton>
          </S.TextContent>
          <S.ContinueButton
            isMobile={isMobile}
            onClick={() => setIsShowing(false)}>
            <span>Continue to dashboard</span>
            <SVG icon={faArrowRight as Icon} size="sm" />
          </S.ContinueButton>
        </S.ContentGrid>
      </S.ModalContainer>
    </Modal>
  );
}
